import {
  retrieveFile,
  retrieveFileTree,
  updateFile,
  fileDestination,
} from "@/lib/data/requests/files/canonical-files";
import {
  adaptFile,
  mergeFileTree,
} from "@/lib/data/requests/files/file-adapter";
import {
  authenticatedFastApiAxiosInstance,
  authenticatedFastApiAxiosInstanceAllowEmptyString,
} from "@/lib/data/http";
import { baseFastApiMatterRoute } from "@/lib/data/requests/matters/matter";
import { type APIResponse } from "@/lib/types";
import {
  type MatterFile,
  type MatterFileReference,
} from "./../../../types/Matter";

const MAX_MATTER_FILE_PAGES = 50;

export type MatterFileParentInput =
  | { matterId: string; massTortId?: never }
  | { matterId?: never; massTortId: string };

export type MatterFileParentIdentifier = string | MatterFileParentInput;

type MatterFileBodyInput = Omit<Partial<MatterFile>, "matterId"> &
  MatterFileParentInput;

type CreateMatterFileInput = MatterFileParentInput &
  Pick<MatterFile, "isFolder" | "idLocation" | "name"> & { file?: File } & Omit<
    Partial<MatterFile>,
    "matterId"
  > & {
    matterFileReviewerId?: string;
  };

const matterFileReferencePayloadKeys = [
  "fileId",
  "matterId",
  "emailId",
  "expenseId",
  "incidentReportId",
  "insuranceId",
  "lienId",
  "litigationDateId",
  "lostWagesId",
  "medicalTreatmentId",
  "medicalTreatmentAppointmentId",
  "medicalTreatmentBalanceConfirmationId",
  "medicalTreatmentBillId",
  "medicalTreatmentPlanId",
  "medicalTreatmentRecordId",
  "propertyDamageId",
  "settlementCounterId",
  "settlementDemandId",
  "settlementOfferId",
  "shareFolderId",
  "signatureRequestId",
  "statuteOfLimitationsId",
  "trustCheckId",
  "vehicleId",
] as const satisfies readonly (keyof MatterFileReference)[];

const getMatterFileReferencePayload = (
  data: Pick<MatterFileReference, "matterId" | "fileId"> &
    Partial<MatterFileReference>,
) =>
  Object.fromEntries(
    matterFileReferencePayloadKeys
      .filter((key) => data[key] !== undefined)
      .map((key) => [key, data[key]]),
  );

// File responses include read-only metadata. Only send fields accepted by the
// canonical file API, even when a caller passes an entire cached file record.
const matterFileWriteKeys = new Set([
  "artifactId",
  "description",
  "idLocation",
  "isFolder",
  "massTortId",
  "matterId",
  "name",
  "presignedUrlKey",
  "tags",
  "variables",
]);

const cleanMatterFileBody = (data: object, update = false) =>
  Object.fromEntries(
    Object.entries(data).filter(
      ([key, value]) =>
        value !== undefined &&
        !(
          key === "description" &&
          typeof value === "string" &&
          !value.trim()
        ) &&
        !(key === "tags" && Array.isArray(value) && value.length === 0) &&
        (matterFileWriteKeys.has(key) ||
          (update && (key === "id" || key === "dateModified"))),
    ),
  );

const getMatterFileParentId = (data: MatterFileParentIdentifier) =>
  typeof data === "string"
    ? data
    : "massTortId" in data
      ? data.massTortId
      : data.matterId;

export async function retrieveMatterFileMetadata(
  parent: MatterFileParentIdentifier,
) {
  const parentId = getMatterFileParentId(parent);
  let cursor: string | undefined;
  let pageCount = 0;
  let latestMeta: APIResponse<MatterFile[]>["meta"] = { userMsg: "" };
  const files: MatterFile[] = [];

  do {
    const response = await authenticatedFastApiAxiosInstance.get<
      APIResponse<MatterFile[]>
    >(`${baseFastApiMatterRoute}/${parentId}/files`, {
      params: { cursor, limit: 500 },
    });

    files.push(...response.data.data);
    latestMeta = response.data.meta;
    const pagination = response.data.pagination as
      | { cursor?: string; lastEvaluatedKey?: string; nextCursor?: string }
      | undefined;
    cursor =
      pagination?.cursor ??
      pagination?.nextCursor ??
      pagination?.lastEvaluatedKey;
    pageCount += 1;
  } while (cursor && pageCount < MAX_MATTER_FILE_PAGES);

  if (cursor)
    throw new Error("Unable to finish loading files. Please refresh.");
  return { data: files, meta: latestMeta } satisfies APIResponse<MatterFile[]>;
}

export async function retrieveMatterFiles(parent: MatterFileParentIdentifier) {
  const scope =
    typeof parent !== "string" && parent.massTortId ? "mass_tort" : "matter";
  const [metadata, canonical] = await Promise.all([
    retrieveMatterFileMetadata(parent),
    retrieveFileTree(
      fileDestination(scope, null, getMatterFileParentId(parent)),
    ),
  ]);
  return { ...metadata, data: mergeFileTree(canonical, metadata.data) };
}

export const createMatterFileV1 = (data: CreateMatterFileInput) => {
  const body = cleanMatterFileBody(data);
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .post<APIResponse<MatterFile>>(`${baseFastApiMatterRoute}/files`, body)
    .then((res) => res.data);
};

export const updateMatterFileV1 = async (
  data: Pick<MatterFile, "id" | "dateModified"> & MatterFileBodyInput,
) => {
  const body = cleanMatterFileBody(data, true);
  if (
    data.revision !== undefined &&
    data.presignedUrlKey === undefined &&
    !("artifactId" in data) &&
    data.tags === undefined &&
    data.variables === undefined
  ) {
    const current = await retrieveFile(data.id);
    if (
      "idLocation" in data &&
      (data.idLocation || null) !== current.parentId
    ) {
      return authenticatedFastApiAxiosInstanceAllowEmptyString
        .patch<APIResponse<MatterFile>>(`${baseFastApiMatterRoute}/files`, body)
        .then((res) => res.data);
    }
    const file = await updateFile(data.id, {
      revision: data.revision,
      name: data.name,
      description: data.description,
    });
    return {
      data: adaptFile<MatterFile>(file, data),
      meta: { userMsg: "File updated" },
    };
  }
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .patch<APIResponse<MatterFile>>(`${baseFastApiMatterRoute}/files`, body)
    .then((res) => res.data);
};

export const createMatterFileReferenceV2 = (
  data: Pick<MatterFileReference, "matterId" | "fileId"> &
    Partial<MatterFileReference>,
) => {
  return authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterFileReference>
    >(`${baseFastApiMatterRoute}/files/references`, getMatterFileReferencePayload(data))
    .then((res) => res.data);
};

export const deleteMatterFileReferenceV2 = (
  data: Pick<MatterFileReference, "matterId" | "id">,
) => {
  return authenticatedFastApiAxiosInstance
    .delete<
      APIResponse<MatterFileReference>
    >(`${baseFastApiMatterRoute}/files/references/${data.id}`)
    .then((res) => res.data);
};

export type MergeMatterFilesInput = MatterFileParentInput & {
  files: string[];
  name: string;
};

export const mergeMatterFilesV2 = (data: MergeMatterFilesInput) => {
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .post<
      APIResponse<MatterFile>
    >(`${baseFastApiMatterRoute}/files/merge`, data)
    .then((res) => res.data);
};

export const duplicateMatterFileV1 = (data: {
  matterId: string;
  id: string;
  name: string;
  idLocation?: string;
}) => {
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .post<
      APIResponse<MatterFile>
    >(`${baseFastApiMatterRoute}/files/duplicate`, data)
    .then((res) => res.data);
};

export type GenerateMatterFilePdfInput = MatterFileParentInput & {
  matterFileId: string;
  matterFileName: string;
};

const serializeGenerateMatterFilePdfPayload = (
  data: GenerateMatterFilePdfInput,
) => ({
  id: data.matterFileId,
  massTortId: data.massTortId,
  matterId: data.matterId,
  name: data.matterFileName,
});

export const generateMatterFilePdf = (data: GenerateMatterFilePdfInput) => {
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .post<
      APIResponse<MatterFile>
    >(`${baseFastApiMatterRoute}/files/generate-pdf`, serializeGenerateMatterFilePdfPayload(data))
    .then((res) => res.data);
};
